import type { BYOKProvider, RouteChange } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { t } from '../i18n';
import type { KeyRow } from '../lib/keys-types';
import type { RouteWire } from '../lib/llmRoutes';
import { AgentKeyLabelField } from './AgentKeyLabelField';
import { Field } from './Field';
import { ProviderSelect } from './ProviderSelect';
import { RouteEffortField } from './RouteEffortField';

// The Advanced drawer's shared route-fields block — thinking depth, a specific
// key and a provider limit (#5211's org fieldset, moved here so it has one
// home instead of three: the org purpose, a per-repo row, and a Coder
// task-tag row, #5436). Moved out of `AgentAdvancedDrawer.tsx`, not copied:
// this is the SAME `<fieldset>` that used to live there, the locked note
// included, so `canEdit=false` disables the whole group in one place
// regardless of which of the three sites mounts it.
//
// It emits `RouteChange`s only (`onChange`) and never writes anything itself
// — every write is `ModelSaves.choose` (org purpose, org task-tag row) or
// `RepoRouteSaves.choose` (a repo row or the repo add slot), the caller's own.
//
// `idPrefix` is the caller's own namespace (`agent-drawer-<agent>`,
// `agent-drawer-task-tag-<purpose>`, `agent-drawer-repo-<purpose>-<repoId>`,
// `agent-drawer-repo-add-<purpose>-fields`) — every id this component mints
// (`-effort`, `-key`, `-key-rename`, `-provider`) is unique per mount as long
// as the caller's prefix is.

export type AgentRouteAdvancedFieldsProps = {
  readonly idPrefix: string;
  readonly route: RouteWire | undefined;
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly canEdit: boolean;
  /** The route write is in flight — only the key field reads this (the
   * original fieldset's own behaviour; effort/provider read `canEdit` alone). */
  readonly busy: boolean;
  readonly onChange: (change: RouteChange) => void;
  readonly token: string | null;
  /** Re-read after a key rename — it may have moved this route's own pin. */
  readonly onKeyRenamed: () => void;
};

export function AgentRouteAdvancedFields(props: AgentRouteAdvancedFieldsProps): JSX.Element {
  /** A model-pinned row owns its provider (`route-row.ts`'s `nextRouteRow`) — the
   * provider select is read-only rather than misreporting "Any provider". */
  const modelPinned = (): boolean =>
    props.route?.model !== null && props.route?.model !== undefined;

  return (
    <fieldset disabled={!props.canEdit} class="field-group agent-drawer__route-fields">
      {/* `disabled` on a `<fieldset>` cascades natively to every descendant
          form control (the select, the rename input+button, the provider
          select) regardless of an intermediate component's own props — the
          one seam that covers AgentKeyLabelField's inline rename form too,
          which nothing else here reaches. AC8: a viewer/member (no
          `write:billing`) gets locked controls, never a submittable one. */}
      <Show when={!props.canEdit}>
        <p class="field__desc">{t('agents.picker.locked')}</p>
      </Show>
      <RouteEffortField
        id={`${props.idPrefix}-effort`}
        value={props.route?.reasoning_effort ?? ''}
        error={null}
        disabled={!props.canEdit}
        onChange={(effort) => props.onChange({ kind: 'effort', effort: effort || null })}
      />

      <AgentKeyLabelField
        idPrefix={props.idPrefix}
        keys={props.keys.data ?? []}
        value={props.route?.key_label ?? null}
        canEdit={props.canEdit}
        busy={props.busy}
        onChoose={(label) => props.onChange({ kind: 'keyLabel', keyLabel: label })}
        token={props.token}
        onRenamed={props.onKeyRenamed}
      />

      <Field
        label={t('agents.drawer.provider_limit')}
        id={`${props.idPrefix}-provider`}
        desc={modelPinned() ? t('agents.drawer.provider_limit_model_pinned') : undefined}
      >
        {(a11y) => (
          <ProviderSelect
            {...a11y}
            label={t('agents.drawer.provider_limit')}
            // The route owns its provider whether or not a model is ALSO
            // pinned (`nextRouteRow` never clears one without the other) —
            // `modelPinned()` only gates whether the select stays live, a
            // provider-only pin must still show, never fall back to "Any
            // provider" the moment the drawer reopens.
            value={props.route?.provider ?? ''}
            disabled={!props.canEdit || modelPinned()}
            onChange={(provider) => {
              // A model pin owns its provider (`route-row.ts`) — a change event
              // that reaches here anyway (bypassing the `disabled` select, which
              // is the real guard) must not write a cross-provider pin the
              // resolver refuses.
              if (!props.canEdit || modelPinned()) return;
              props.onChange({
                kind: 'provider',
                provider: provider === '' ? null : (provider as BYOKProvider),
              });
            }}
          />
        )}
      </Field>
    </fieldset>
  );
}
